<template>
  <!-- based on https://bootstrap-vue.org/docs/components/form-tags -->
  <b-form-group
    :label="label"
    label-for="user-tags"
    :state="state"
  >
    <template
      v-if="invalidCoreIds.length > 0"
      #invalid-feedback
    >
      The following CoreIDs are invalid: {{ invalidCoreIds.join(', ') }}.
    </template>
    <template
      v-else-if="['US Approver', 'Security/Compliance Champion', 'PDM', 'Dev Manager'].includes(label)"
      #invalid-feedback
    />
    <template
      v-else
      #invalid-feedback
    >
      Please insert at least {{ minimumUsers }} valid CoreID<span v-if="minimumUsers > 1">s</span>.
    </template>

    <b-form-tags
      :state="state"
      :tag-validator="validator"
      :value="users"
      separator=" ,"
      @input="$emit('input', $event)"
    >
      <template #default="{ tags, inputAttrs, inputHandlers, removeTag }">
        <b-input-group aria-controls="user-tag-input-list">
          <input
            class="form-control"
            placeholder="Type in a CoreID and press comma, space, or enter to add"
            v-bind="inputAttrs"
            :disabled="disabled"
            v-on="inputHandlers"
          >
          <b-input-group-append>
            <b-button
              v-b-tooltip.hover
              title="Copy CoreIDs to clipboard"
              @click="copyUsersToClipboard"
            >
              <font-awesome-icon :icon="['fa', 'clipboard']" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <ul
          id="user-tag-input-list"
          class="list-unstyled d-inline-flex flex-wrap mb-0"
          aria-live="polite"
          aria-relevant="additions removals"
        >
          <b-card
            v-show="users.length === 0"
            class="mt-1 mr-1"
            body-class="py-1 pr-4 text-nowrap"
          >
            <strong>No users added</strong>
          </b-card>
          <!-- Always use the tag value as the :key, not the index! -->
          <!-- Otherwise screen readers will not read the tag
                additions and removals correctly -->
          <b-card
            v-for="tag in sortTags(tags)"
            :id="`user-tag-input-tag_${tag.replace(/\s/g, '_')}_`"
            :key="tag"
            tag="li"
            class="mt-1 mr-1"
            body-class="py-1 pr-2 text-nowrap"
          >
            <strong>{{ tag }} {{ getNamesFromCoreIds(tag.toUpperCase()) }}</strong>
            <b-button
              :aria-controls="`user-tag-input-tag_${tag.replace(/\s/g, '_')}_`"
              size="sm"
              variant="link"
              :disabled="disabled"
              @click="removeTag(tag)"
            >
              remove
            </b-button>
          </b-card>
        </ul>
      </template>
    </b-form-tags>
  </b-form-group>
</template>

<script>
import api from '@/api';
import {StakeHoldersMaxLimit} from '../constants/StakeHoldersMaxLimit';

export default {
  name: 'UsersTagInput',
  model: {
    prop: 'users',
    event: 'input',
  },
  props: {
    label: {
      required: true,
      type: String,
    },
    minimumUsers: {
      type: Number,
      default: 0,
    },
    maximumUsers: {
      type: Number,
      default: StakeHoldersMaxLimit.defaultMaximumUsers,
    },
    maximumUsapprovers: {
      type: Number,
      default: StakeHoldersMaxLimit.UsApprover,
    },
    users: {
      required: true,
      type: Array,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      invalidCoreIds: [],
      userNames: {},
    };
  },
  asyncComputed: {
    async state() {
      const stakeHoldersLabel = ['Security/Compliance Champion', 'PDM', 'Dev Manager'];
      const UsApproversLabel = ['US Approver'];
      if (this.users.length < this.minimumUsers) {
        return false;
      }
      if (UsApproversLabel.includes(this.label) && this.users.length > this.maximumUsapprovers) {
        return false;
      }
      if (this.users.length > this.maximumUsers && stakeHoldersLabel.includes(this.label)) {
        return false;
      }
      this.invalidCoreIds = await api.validateCoreIds(this.users);
      this.userNames = await api.getNameFromCoreId(this.users);
      return this.invalidCoreIds.length === 0;
    },
  },
  methods: {
    copyUsersToClipboard() {
      const sortedUsers = Array.from(this.users).sort();
      navigator.clipboard.writeText(sortedUsers.join(','));
    },
    sortTags(tags) {
      tags.sort();
      return tags;
    },
    getNamesFromCoreIds(tag) {
      if (tag in this.userNames) {
        return `- ${this.userNames[tag]}`;
      }
    },
    validator(newUser) {
      for (const user of this.users) {
        if (newUser.toLowerCase() === user.toLowerCase()) {
          return false;
        }
      }
      return true;
    },
  },
};
</script>
